import { HUD, type ModerationAction } from "odyssey-shared";
import type { WindowBox } from "../../ui/kit/index.ts";
import { say } from "../../words.ts";
import { muteTime } from "../chat/words.ts";
import type { ListedRow } from "./client.ts";
import { lengthMinutes } from "./command.ts";

/**
 * The Moderation window's measures and the pure rules its parts share (ADR-056's amendment of 2026-10-06, decisions
 * 6 and 14; the design's `moderation-window` card, a visual reference): the window wide, with the Online players
 * column beside the tabs, wherever 612 logical pixels fit inside the HUD's margins, and narrow otherwise, with
 * Online as the first tab; how a length chosen in the form is read; and how a standing row's time is worded.
 */

/**
 * The window's measures, in logical pixels: the wide window and the narrow one, the Online column's width, the gap
 * between the column and the tabs, the gaps inside a pane, the most characters a reason and a staff note take, and
 * the width of the custom length field.
 */
export const MODERATION_LAYOUT = {
  wide: { width: 612, height: 272 },
  narrow: { width: 300, height: 236 },
  column: 150,
  columnGap: 6,
  gap: 4,
  smallGap: 3,
  textLimit: 200,
  lengthField: 30,
  confirmWidth: 290,
  endWidth: 250,
} as const;

/**
 * The narrowest content that still holds the Online column beside the tabs: below it the window is narrow.
 */
export const WIDE_CONTENT = 500;

/**
 * Where the window sits in the page's logical area and how large it is: the wide window where 612 fits inside the
 * HUD's margins, the narrow one otherwise, each no larger than the area less the margins, in the middle across, and
 * down at the wide window's top where it fits, so the narrow window sits in the card's centre slot.
 *
 * @param area The area's size in logical pixels, as measured; a fraction of a pixel is dropped.
 * @param narrow Whether the narrow window is drawn whatever the room, as the gallery draws the card's narrow states.
 * @returns The window's box in whole logical pixels.
 * @example
 * moderationWindowBox({ width: 640, height: 360 });
 * // { left: 14, top: 44, width: 612, height: 272 }
 */
export function moderationWindowBox(area: { readonly width: number; readonly height: number }, narrow = false): WindowBox {
  const across = Math.floor(area.width);
  const down = Math.floor(area.height);
  const room = across - 2 * HUD.margin;
  const size = room >= MODERATION_LAYOUT.wide.width && !narrow ? MODERATION_LAYOUT.wide : MODERATION_LAYOUT.narrow;
  const width = Math.max(0, Math.min(size.width, room));
  const height = Math.max(0, Math.min(size.height, down - 2 * HUD.margin));
  const top = Math.min(Math.floor((down - height) / 2), Math.floor((down - MODERATION_LAYOUT.wide.height) / 2));
  return { left: Math.max(HUD.margin, Math.floor((across - width) / 2)), top: Math.max(HUD.margin, top), width, height };
}

/**
 * The actions that take a length.
 */
export type TimedAction = "freeze" | "ban" | "feedbackpause";

/**
 * Whether an action takes a length.
 *
 * @param action The action.
 * @returns True for a freeze, a ban and a feedback pause.
 */
export function isTimed(action: ModerationAction): action is TimedAction {
  return action === "freeze" || action === "ban" || action === "feedbackpause";
}

/**
 * The length each timed action starts on: 15 minutes, 7 days and 1 day.
 */
export const DEFAULT_LENGTH: Readonly<Record<TimedAction, string>> = { freeze: "15", ban: "7", feedbackpause: "1" };

/**
 * The minutes a length chosen in the form comes to: a preset's value or a number typed in the action's unit
 * (minutes for a freeze, days for a ban and a pause), `perm` for a permanent ban and `h` for an hour's pause, read
 * as the chat commands read a length.
 *
 * @param action The action.
 * @param choice The preset's value or the number typed.
 * @returns The minutes, 0 for a permanent ban; or null when the length is not one the action takes.
 * @example
 * chosenMinutes("ban", "7");
 * // 10080
 */
export function chosenMinutes(action: TimedAction, choice: string): number | null {
  const typed = choice.trim();
  if (!/^(\d{1,3}|perm|h)$/.test(typed)) {
    return null;
  }
  const word = action === "freeze" ? (typed === "perm" || typed === "h" ? "" : `${typed}m`) : typed === "perm" ? (action === "ban" ? "perm" : "") : typed === "h" ? (action === "feedbackpause" ? "1h" : "") : `${typed}d`;
  if (word === "") {
    return null;
  }
  const minutes = lengthMinutes(action, word);
  return minutes === undefined ? null : minutes;
}

/**
 * A length in words, as a button and the ban's confirmation say it: "15 min", "7 days", "1 day", "1 hour" or
 * "Permanent".
 *
 * @param minutes The minutes, 0 for permanent.
 * @returns The words.
 */
export function lengthWords(minutes: number): string {
  if (minutes === 0) {
    return say("moderation.window.permanent");
  }
  if (minutes < 60) {
    return say("moderation.window.lengths.minutes", { minutes: String(minutes) });
  }
  if (minutes % 1440 === 0) {
    return say("moderation.window.lengths.days", { count: minutes / 1440 });
  }
  return minutes === 60 ? say("moderation.window.lengths.hour") : muteTime(minutes * 60_000);
}

/**
 * How long a standing row has left, in words: "12 min left", "8 days left", or "Permanent".
 *
 * @param until The row's end, or null for none.
 * @param now The page's clock.
 * @returns The words.
 */
export function leftWords(until: number | null, now: number): string {
  return until === null ? say("moderation.window.permanent") : say("moderation.window.left", { time: muteTime(Math.max(0, until - now)) });
}

/**
 * The bans and freezes that stand, soonest to end first and a permanent one last, the newest first among equals.
 *
 * @param rows The rows as listed.
 * @returns The rows in that order.
 */
export function soonestFirst(rows: readonly ListedRow[]): ListedRow[] {
  return [...rows].sort((a, b) => (a.until ?? Number.POSITIVE_INFINITY) - (b.until ?? Number.POSITIVE_INFINITY) || b.at - a.at);
}

/**
 * What a row of the history says happened: "Warned", "Frozen, 15 min", "Banned, 10 days", "Banned, permanent",
 * "Flag", "Ended", or a feedback access action's name.
 *
 * @param row The row.
 * @returns The words.
 */
export function historyWhat(row: ListedRow): string {
  const length = row.until === null ? null : lengthWords(Math.max(1, Math.round((row.until - row.at) / 60_000)));
  switch (row.action) {
    case "kick":
      return say("moderation.window.history.kick");
    case "warn":
      return say("moderation.window.history.warn");
    case "alert":
      return say("moderation.window.history.alert");
    case "lift":
      return say("moderation.window.history.lift");
    case "freeze":
      return say("moderation.window.history.freeze", { length: length ?? say("moderation.window.permanent") });
    case "ban":
      return length === null ? say("moderation.window.history.banPermanent") : say("moderation.window.history.ban", { length });
    default:
      return say(`moderation.actions.${row.action}`);
  }
}
